import React, { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import {
  ClassHealth,
  ClassHealthView,
  ClassServiceQuality,
  EvidenceDrillDown,
  EvidenceObligation,
  IntegrityBreak,
  IntegrityCheckType,
  IntegrityOverview,
  RatingDimension,
  RatioMetric,
  ServiceQualityView,
  getClassHealth,
  RubricHistory,
  getEnrollmentEvidence,
  getIntegrityOverview,
  getRubricHistory,
  requestEvidence,
  runIntegrityChecks,
} from '../api/integrity';
import {
  Drawer,
  Empty,
  Field,
  Kpi,
  KpiRow,
  Note,
  OpsPage,
  Pill,
  PillTone,
  Row,
  Section,
  Table,
  Value,
} from '../../../design-system/ops';
import {
  QUALITY_STATUS_LABEL,
  ServiceQualityPanel,
  qualityLabel,
} from '../components/ServiceQualityPanel';
import { DefinitionTag } from '../components/MetricDefinition';
import { UTC_ZONE, formatShortDateTime } from '../time/zonedTime';
import { closingLabel } from '../vocabulary/learningVocabulary';

/**
 * UC-A03 — evidence and progress integrity, and class health (ADM-13).
 *
 * What the screen can do is deliberately small. It shows what the integrity
 * run found, which payments are held because of it, and the deadline each
 * evidence request is running against; and it can ask a teacher for the
 * missing piece. It cannot edit attendance, a rubric or a recording, lift a
 * hold, or move money — a hold lifts when the next run finds the evidence
 * complete (ADM-13-FR-07). Saying so on the screen is part of the design: an
 * operator who thinks there should be an "approve" button will go looking for
 * a database console instead.
 */

const CHECK_LABELS: Record<IntegrityCheckType, string> = {
  MILESTONE_VS_PAYMENT: 'Hito vs pago',
  SETTLEMENT_VS_TEACHER_VIEW: 'Liquidación vs vista del profesor',
  HOLDBACK_VS_DISPUTE: 'Retención final vs disputa',
  BOOKING_VS_MILESTONE: 'Reserva vs sesión',
  EVIDENCE_VS_STATUS: 'Evidencia vs estado',
  RECORDING_VS_COMPLETION: 'Grabación vs cierre',
  ENROLLMENT_VS_MILESTONES: 'Inscripción vs hitos',
  RELEASE_VS_EVIDENCE: 'Pago vs evidencia',
  RUBRIC_AFTER_RELEASE: 'Rúbrica después del pago',
  FORMAL_RESULT_VS_PROFILE: 'Resultado formal en un curso sin evaluación',
  PARENT_PROGRESS_AUDIT: 'Progreso familiar vs registros',
};

const FINDING_LABELS: Record<string, string> = {
  MISSING_REQUIRED_EVIDENCE: 'Falta evidencia',
  AMBIGUOUS_EVIDENCE: 'Evidencia ambigua',
  VERSION_MISMATCH: 'Registros no coinciden',
  STALE_PROJECTION: 'Proyección desactualizada',
  PROVIDER_DELAY: 'Proveedor pendiente',
  INCOMPATIBLE_RESULT: 'Resultado incompatible con el curso',
};

const OBLIGATION_LABELS: Record<string, string> = {
  TEACHER_CHECK_IN: 'Entrada del profesor',
  MIN_DURATION: 'Duración mínima',
  RECORDING: 'Grabación',
  ATTENDANCE: 'Asistencia',
  RUBRIC: 'Rúbrica',
  DELIVERABLE: 'Entrega final',
  FINAL_RUBRIC: 'Rúbrica final',
};

const STATE_PILL: Record<EvidenceObligation['state'], { tone: PillTone; label: string }> = {
  COMPLETE: { tone: 'ok', label: 'Completa' },
  PENDING: { tone: 'warn', label: 'Pendiente' },
  MISSING: { tone: 'bad', label: 'Falta' },
  AMBIGUOUS: { tone: 'warn', label: 'Ambigua' },
  N_A: { tone: 'neu', label: 'No aplica' },
};

const fmt = (iso?: string) => (iso ? new Date(iso).toLocaleString('es-MX') : '—');

const subjectOf = (b: { sequence?: number; subjectKey?: string }) =>
  b.subjectKey === 'PROGRESS'
    ? 'Progreso familiar'
    : b.sequence === undefined
    ? 'Inscripción'
    : b.sequence === 8
    ? closingLabel()
    : `Sesión ${b.sequence}`;

/** A ratio as the console shows it: percentage with its counts, or N/A for 0/0. */
export function ratioLabel(m: RatioMetric): string {
  if (m.status === 'N_A' || m.value === null) return 'N/A';
  return `${Math.round(m.value * 100)}% (${m.numerator}/${m.denominator})`;
}

/** A review average on its own scale. Never converted to a percentage. */
export function ratingLabel(r: RatingDimension): string {
  if (r.averageRating === null || r.totalReviews === 0) return 'Sin reseñas';
  return `${r.averageRating.toFixed(1)} / 5 (${r.totalReviews})`;
}

/** ADM-13-FR-08 for one class: confirmed quality, never without its response coverage. */
const ClassQualityCell: React.FC<{ n: ClassServiceQuality }> = ({ n }) =>
  n.status !== 'MEASURED' ? (
    <span className="mut">Encuestas no disponibles</span>
  ) : (
    <span>
      {qualityLabel(n.confirmedQuality)}
      <br />
      <span className="mut">
        Cobertura {qualityLabel(n.responseCoverage)} · {QUALITY_STATUS_LABEL(n.resultStatus)}
      </span>
    </span>
  );

const PRESENCE_LABEL = {
  CONNECTED: 'conectado',
  NEVER_CONNECTED: 'nunca se conectó',
  PENDING: 'pendiente',
  UNKNOWN: 'sin confirmar',
} as const;

const DeadlineCell: React.FC<{ b: IntegrityBreak }> = ({ b }) => {
  if (!b.evidenceDueAt) return <span className="mut">Sin solicitud</span>;
  if (b.escalatedAt || b.deadlineState === 'EXPIRED') {
    return <Pill tone="bad">Vencido · {fmt(b.evidenceDueAt)}</Pill>;
  }
  return <Pill tone="warn">Vence {fmt(b.evidenceDueAt)}</Pill>;
};

/** New timestamps on this screen, in UTC and labelled so, on the shared formatter. */
const utc = (iso?: string) => (iso ? `${formatShortDateTime(iso, UTC_ZONE)} UTC` : '—');

/**
 * Whether this milestone has a rubric history worth asking for: one was
 * written. Since MC-01 v1-7 no rubric is a required obligation in any
 * profile, so the obligation rows can no longer say it.
 */
const rubricApplies = (m: { facts: { rubricPresent: boolean } }) => m.facts.rubricPresent;

/** ADM-03-FR-08 — versions, actors and reasons. Read-only: no score, no text. */
const RubricHistoryList: React.FC<{ history: RubricHistory }> = ({ history }) => {
  if (history.applicability === 'N_A') {
    return <Note>No aplica: este perfil no usa rúbrica.</Note>;
  }
  if (history.versions.length === 0) {
    return <Note>Sin versiones de rúbrica registradas.</Note>;
  }
  return (
    <ul aria-label={`Historial de rúbrica · ${subjectOf(history)}`}>
      {history.versions.map((v) => (
        <li key={v.version}>
          v{v.version}
          {v.priorVersion ? ` (corrige v${v.priorVersion})` : ''} · {v.actorId} ·{' '}
          {utc(v.recordedAt)}
          {v.afterRelease ? ' · después del pago' : ''} ·{' '}
          {v.correctionReason ? `Motivo: ${v.correctionReason}` : 'Primera versión'}
        </li>
      ))}
    </ul>
  );
};

const EvidenceDrawer: React.FC<{
  enrollmentId: string | null;
  onClose: () => void;
}> = ({ enrollmentId, onClose }) => {
  const [view, setView] = useState<EvidenceDrillDown | null>(null);
  const [error, setError] = useState<string | null>(null);
  // ADM-03-FR-04: the drill-down is audited, and opens only with a stated reason.
  const [reasonDraft, setReasonDraft] = useState('');
  const [reason, setReason] = useState<string | null>(null);
  const [histories, setHistories] = useState<Record<number, RubricHistory | 'error'>>({});

  useEffect(() => {
    setReason(null);
    setReasonDraft('');
    setView(null);
    setError(null);
    setHistories({});
  }, [enrollmentId]);

  useEffect(() => {
    if (!enrollmentId || !reason) return undefined;
    let cancelled = false;
    setView(null);
    setError(null);
    getEnrollmentEvidence(enrollmentId, reason)
      .then((v) => !cancelled && setView(v))
      .catch((e: any) => !cancelled && setError(e?.message || 'No se pudo cargar la evidencia'));
    return () => {
      cancelled = true;
    };
  }, [enrollmentId, reason]);

  const openHistory = (sequence: number) => {
    if (!enrollmentId || !reason) return;
    getRubricHistory(enrollmentId, sequence, reason)
      .then((h) => setHistories((all) => ({ ...all, [sequence]: h })))
      .catch(() => setHistories((all) => ({ ...all, [sequence]: 'error' })));
  };

  return (
    <Drawer
      open={Boolean(enrollmentId)}
      onClose={onClose}
      title="Evidencia de la inscripción"
      meta={enrollmentId ?? undefined}
      footer={
        view ? (
          <Link className="btn btn-o" to={view.supportCaseRoute}>
            Abrir caso de soporte
          </Link>
        ) : undefined
      }
    >
      {!reason ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (reasonDraft.trim()) setReason(reasonDraft.trim());
          }}
        >
          <Note>
            Ver la evidencia queda registrado en la auditoría con tu nombre y el motivo que
            escribas.
          </Note>
          <label htmlFor="evidence-reason">Motivo de la consulta</label>
          <textarea
            id="evidence-reason"
            value={reasonDraft}
            maxLength={500}
            onChange={(e) => setReasonDraft(e.target.value)}
          />
          <button className="btn btn-p" type="submit" disabled={!reasonDraft.trim()}>
            Ver evidencia
          </button>
        </form>
      ) : null}
      {error ? <Note tone="bad">{error}</Note> : null}
      {reason && !view && !error ? <Note>Cargando…</Note> : null}
      {view ? (
        <>
          <Note>
            Solo metadatos: estados, horas y referencias. Sin notas, textos de rúbrica ni enlaces a
            grabaciones.
          </Note>
          {view.lineage ? (
            <Field label="Versiones y correlación">
              <Value>
                <span className="mut">
                  Curso {view.lineage.courseVersionRef ?? 'sin versión registrada'} · Política{' '}
                  {view.lineage.learningPolicyVersion} · Parámetros{' '}
                  {view.lineage.obligationPolicy.parameterVersion || 'valores por defecto'} ·
                  Progreso{' '}
                  {view.lineage.progressProjection
                    ? `v${view.lineage.progressProjection.projectionVersion} (${utc(
                        view.lineage.progressProjection.asOf,
                      )})`
                    : 'sin calcular'}{' '}
                  · Correlación {view.lineage.correlationId}
                </span>
              </Value>
            </Field>
          ) : null}
          {view.enrollmentBreak ? (
            <Note tone="warn">{view.enrollmentBreak.findings.map((f) => f.detail).join(' ')}</Note>
          ) : null}
          {view.milestones.map((m) => (
            <Field key={m.milestoneId} label={`${subjectOf(m)} · ${m.status}`}>
              <Value>
                {m.obligations.map((o) => (
                  <span key={o.type}>
                    <Pill tone={STATE_PILL[o.state].tone}>
                      {OBLIGATION_LABELS[o.type] ?? o.type}: {STATE_PILL[o.state].label}
                      {o.stale
                        ? ' (procesamiento atascado)'
                        : o.providerDelay
                        ? ' (proveedor procesando)'
                        : ''}
                    </Pill>{' '}
                  </span>
                ))}
              </Value>
              <Value>
                <span className="mut">
                  Perfil {m.profile.ref ?? 'heredado'} · Reserva {m.bookingState ?? 'ninguna'} ·
                  Entrada profesor {fmt(m.facts.teacherCheckedInAt)} · Alumno{' '}
                  {m.facts.learnerAttended ? 'asistió' : 'no asistió'} · Duración{' '}
                  {m.facts.durationMinutes ?? '—'} min · Grabación{' '}
                  {m.facts.recordingPresent ? m.facts.recordingStatus ?? 'registrada' : 'no hay'} ·
                  Rúbrica {m.facts.rubricPresent ? fmt(m.facts.rubricSubmittedAt) : 'no hay'}
                </span>
              </Value>
              {m.presence ? (
                <Value>
                  <span className="mut">
                    Conexión {PRESENCE_LABEL[m.presence.connection.status]} (
                    {Math.round(m.presence.connection.connectedSeconds / 60)} min) · Identidad no
                    verificada ({m.presence.identity.openGaps.join(', ')}) · Asistencia completa:
                    sin umbral definido
                  </span>
                  {m.presence.association.review ? (
                    <>
                      {' '}
                      <Pill tone="warn">
                        Revisar acceso: {m.presence.association.unassociatedAttendees} conexión(es)
                        no asociada(s)
                      </Pill>
                    </>
                  ) : null}
                </Value>
              ) : null}
              {m.hold ? (
                <Value>
                  <Pill tone="bad">Pago retenido desde {fmt(m.hold.placedAt)}</Pill>
                </Value>
              ) : null}
              {m.deadline ? (
                <Value>
                  <Pill tone={m.deadline.state === 'EXPIRED' ? 'bad' : 'warn'}>
                    Plazo de evidencia: {fmt(m.deadline.dueAt)}
                    {m.deadline.state === 'EXPIRED' ? ' (vencido)' : ''}
                  </Pill>
                </Value>
              ) : null}
              {rubricApplies(m) ? (
                <Value>
                  {histories[m.sequence] === 'error' ? (
                    <Note tone="bad">No se pudo cargar el historial de rúbrica.</Note>
                  ) : histories[m.sequence] ? (
                    <RubricHistoryList history={histories[m.sequence] as RubricHistory} />
                  ) : (
                    <button
                      className="btn btn-o"
                      type="button"
                      onClick={() => openHistory(m.sequence)}
                    >
                      Historial de rúbrica · {subjectOf(m)}
                    </button>
                  )}
                </Value>
              ) : null}
            </Field>
          ))}
        </>
      ) : null}
    </Drawer>
  );
};

const AdminIntegrity: React.FC = () => {
  const [params, setParams] = useSearchParams();
  const [overview, setOverview] = useState<IntegrityOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [health, setHealth] = useState<ClassHealth[] | null>(null);
  const [definitions, setDefinitions] = useState<ClassHealthView['definitions'] | null>(null);
  const [quality, setQuality] = useState<ServiceQualityView | null>(null);
  const [period, setPeriod] = useState<string>('');
  const [healthError, setHealthError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ tone: 'plain' | 'bad'; text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const openEnrollment = params.get('enrollment');
  // DEC-S3-A03-07 — dark until A09 is disposed: the server says so, and neither
  // the section nor the column exists until it says otherwise.
  const activeQuality = quality && quality.status !== 'INACTIVE' ? quality : null;

  const load = useCallback(async () => {
    try {
      setOverview(await getIntegrityOverview());
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'No se pudo cargar la integridad');
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    getClassHealth(period || undefined)
      .then((v) => {
        setHealth(v.classes);
        setDefinitions(v.definitions ?? null);
        setQuality(v.serviceQuality);
        setHealthError(null);
      })
      .catch((e: any) => setHealthError(e?.message || 'No se pudo cargar la salud de las clases'));
  }, [period]);

  const ask = async (b: IntegrityBreak) => {
    if (!b.milestoneId) return;
    setBusy(b.subjectKey);
    try {
      const res = await requestEvidence(b.enrollmentId, b.milestoneId);
      setMessage({
        tone: 'plain',
        text: `Solicitud enviada al profesor. Plazo: ${fmt(res.evidenceDueAt)}.`,
      });
      await load();
    } catch (e: any) {
      setMessage({ tone: 'bad', text: e?.message || 'No se pudo enviar la solicitud' });
    } finally {
      setBusy(null);
    }
  };

  const runNow = async () => {
    setBusy('run');
    try {
      const res = await runIntegrityChecks();
      setMessage({
        tone: 'plain',
        text: `Revisión completada: ${res.runs.length} comprobaciones.`,
      });
      await load();
    } catch (e: any) {
      setMessage({ tone: 'bad', text: e?.message || 'No se pudo ejecutar la revisión' });
    } finally {
      setBusy(null);
    }
  };

  const openDrawer = (enrollmentId: string | null) => {
    const next = new URLSearchParams(params);
    if (enrollmentId) next.set('enrollment', enrollmentId);
    else next.delete('enrollment');
    setParams(next);
  };

  if (error) {
    return (
      <OpsPage title="Integridad de evidencia">
        <Note tone="bad">{error}</Note>
      </OpsPage>
    );
  }
  if (!overview) {
    return (
      <OpsPage title="Integridad de evidencia">
        <Note>Cargando…</Note>
      </OpsPage>
    );
  }

  const requested = overview.breaks.filter((b) => b.evidenceRequestedAt);
  const expired = overview.breaks.filter((b) => b.escalatedAt || b.deadlineState === 'EXPIRED');

  return (
    <OpsPage
      title="Integridad de evidencia"
      subtitle={`Sesión → evidencia → progreso → pago · ${fmt(overview.generatedAt)}`}
      action={
        <button className="btn btn-o" onClick={runNow} disabled={busy === 'run'}>
          {busy === 'run' ? 'Revisando…' : 'Ejecutar revisión ahora'}
        </button>
      }
    >
      <Note>
        Esta pantalla no corrige evidencia ni mueve dinero. Un pago retenido se libera solo cuando
        la siguiente revisión encuentra la evidencia completa; la corrige quien la registró.
      </Note>
      {message ? <Note tone={message.tone}>{message.text}</Note> : null}

      <KpiRow>
        <Kpi
          label="Rupturas abiertas"
          value={overview.breaks.length}
          tone={overview.breaks.length > 0 ? 'warn' : 'neutral'}
        />
        <Kpi
          label="Pagos retenidos"
          value={overview.holds.length}
          tone={overview.holds.length > 0 ? 'bad' : 'neutral'}
          note="Sin evidencia completa no se paga"
        />
        <Kpi label="Solicitudes de evidencia" value={requested.length} />
        <Kpi
          label="Plazos vencidos"
          value={expired.length}
          tone={expired.length > 0 ? 'bad' : 'neutral'}
          note={`Plazo: ${overview.evidenceDeadline.hours} h · ${
            overview.evidenceDeadline.parameterVersion || 'valor por defecto'
          }`}
        />
      </KpiRow>

      <Section title="Rupturas abiertas" count={overview.breaks.length}>
        {overview.breaks.length === 0 ? (
          <Empty title="Sin rupturas" detail="La última revisión no encontró inconsistencias." />
        ) : (
          <Table
            columns="120px 1fr 1.4fr 110px 170px 140px"
            head={['Hito', 'Inscripción', 'Hallazgo', 'Pago', 'Plazo', '']}
          >
            {overview.breaks.map((b) => (
              <Row
                key={`${b.enrollmentId}#${b.subjectKey}`}
                onOpen={() => openDrawer(b.enrollmentId)}
                label={`Ver evidencia de ${subjectOf(b)} en ${b.enrollmentId}`}
              >
                <span>
                  <b>{subjectOf(b)}</b>
                </span>
                <span className="mut">{b.enrollmentId}</span>
                <span>
                  {b.findings.map((f) => (
                    <span key={`${f.checkType}-${f.reason}`}>
                      <Pill tone="warn">{FINDING_LABELS[f.finding ?? ''] ?? f.reason}</Pill>{' '}
                      <span className="mut">{CHECK_LABELS[f.checkType] ?? f.checkType}</span>{' '}
                    </span>
                  ))}
                </span>
                <span>
                  {b.holdPlaced ? <Pill tone="bad">Retenido</Pill> : <Pill>Ya pagado</Pill>}
                </span>
                <span>
                  <DeadlineCell b={b} />
                </span>
                <span>
                  {b.milestoneId ? (
                    <button
                      className="btn btn-o btn-xs"
                      disabled={busy === b.subjectKey}
                      onClick={(e) => {
                        e.stopPropagation();
                        ask(b);
                      }}
                    >
                      {b.evidenceRequestedAt ? 'Recordar al profesor' : 'Pedir evidencia'}
                    </button>
                  ) : null}
                </span>
              </Row>
            ))}
          </Table>
        )}
      </Section>

      <Section title="Pagos retenidos por evidencia" count={overview.holds.length}>
        {overview.holds.length === 0 ? (
          <Empty title="Ningún pago retenido" />
        ) : (
          <Table columns="140px 1fr 1fr 180px" head={['Hito', 'Inscripción', 'Motivo', 'Desde']}>
            {overview.holds.map((h) => (
              <Row key={`${h.enrollmentId}#${h.milestoneId}`}>
                <span>
                  <b>{subjectOf(h)}</b>
                </span>
                <span className="mut">{h.enrollmentId}</span>
                <span className="mut">{h.reasons.join(', ')}</span>
                <span>{fmt(h.placedAt)}</span>
              </Row>
            ))}
          </Table>
        )}
      </Section>

      <Section title="Últimas revisiones" subtitle="Una por comprobación. Se ejecutan cada hora.">
        <Table
          columns="1.4fr 110px 100px 100px 180px"
          head={['Comprobación', 'Estado', 'Revisadas', 'Hallazgos', 'Hora']}
        >
          {overview.latestRuns.map((r) => (
            <Row key={r.checkType}>
              <span>{CHECK_LABELS[r.checkType] ?? r.checkType}</span>
              <span>
                {!r.status ? (
                  <Pill>Sin ejecutar</Pill>
                ) : r.status === 'ok' ? (
                  <Pill tone="ok">Sin hallazgos</Pill>
                ) : r.status === 'mismatch' ? (
                  <Pill tone="warn">Con hallazgos</Pill>
                ) : (
                  <Pill tone="bad">Error</Pill>
                )}
              </span>
              <span>{r.enrollmentsScanned ?? '—'}</span>
              <span>{r.mismatches?.length ?? '—'}</span>
              <span className="mut">{fmt(r.completedAt)}</span>
            </Row>
          ))}
        </Table>
      </Section>

      {activeQuality ? (
        <Section
          title="Ciclos con calidad confirmada por la familia"
          subtitle="Servicio completado, evidencia exigible completa y valoración de 4 o 5, sobre los ciclos terminados del mes."
          action={
            <label>
              Periodo{' '}
              <input
                type="month"
                value={period || activeQuality.period || ''}
                onChange={(e) => setPeriod(e.target.value)}
              />
            </label>
          }
        >
          {healthError ? <Note tone="bad">{healthError}</Note> : null}
          <ServiceQualityPanel quality={activeQuality} />
        </Section>
      ) : null}

      <Section
        title="Salud de las clases"
        subtitle="Calculada en vivo con los mismos registros que ven profesor y familia."
      >
        {healthError ? <Note tone="bad">{healthError}</Note> : null}
        {!health && !healthError ? <Note>Cargando…</Note> : null}
        {health && health.length === 0 ? <Empty title="Todavía no hay clases en curso" /> : null}
        {health && health.length > 0 ? (
          <Table
            columns={`1.3fr repeat(4, 1fr) 90px 1fr 1fr${activeQuality ? ' 120px' : ''}`}
            head={[
              'Clase',
              // ADM-03-FR-09 — each ratio names its definition, version and window.
              <>
                Ciclos completos
                <DefinitionTag stamp={definitions?.cycleCompletion} />
              </>,
              <>
                Asistencia
                <DefinitionTag stamp={definitions?.attendance} />
              </>,
              <>
                Rúbricas
                <DefinitionTag stamp={definitions?.rubricCompletion} />
              </>,
              <>
                Evidencia
                <DefinitionTag stamp={definitions?.evidenceCompliance} />
              </>,
              'Integridad',
              'Reseña del curso',
              'Reseña del profesor',
              ...(activeQuality ? ['Calidad confirmada de la clase'] : []),
            ]}
          >
            {health.map((c) => (
              <Row key={c.courseId}>
                <span>
                  <b>{c.courseTitle ?? c.courseId}</b>
                  <br />
                  <span className="mut">{c.enrollments} inscripciones</span>
                </span>
                <span>{ratioLabel(c.cycleCompletion)}</span>
                <span>{ratioLabel(c.attendance)}</span>
                <span>
                  {ratioLabel(c.rubricCompletion)}
                  {c.rubricCompletion.notApplicable > 0 ? (
                    <>
                      <br />
                      <span className="mut">{c.rubricCompletion.notApplicable} no aplican</span>
                    </>
                  ) : null}
                </span>
                <span>{ratioLabel(c.evidenceCompliance)}</span>
                <span>
                  {c.progressIntegrity.alert ? (
                    <Pill tone="bad">{c.progressIntegrity.openBreaks} abiertas</Pill>
                  ) : (
                    <Pill tone="ok">Sin alertas</Pill>
                  )}
                </span>
                <span>{ratingLabel(c.reviews.course)}</span>
                <span>{ratingLabel(c.reviews.teacher)}</span>
                {activeQuality ? <ClassQualityCell n={c.classQuality} /> : null}
              </Row>
            ))}
          </Table>
        ) : null}
        <Note>
          Las reseñas del curso y del profesor se muestran por separado y en su escala de 1 a 5; no
          se convierten en porcentaje.{' '}
          {activeQuality
            ? 'La calidad confirmada sale de la encuesta privada de cada ciclo, no de las reseñas. '
            : null}
          La analítica pedagógica avanzada (ADM-13-FR-09) es de una etapa posterior y no se calcula
          aquí.
        </Note>
      </Section>

      <EvidenceDrawer enrollmentId={openEnrollment} onClose={() => openDrawer(null)} />
    </OpsPage>
  );
};

export default AdminIntegrity;
